import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../auth/AuthContext'
import InventoryPicker from '../InventoryPicker'
import { ioSendForm } from './api'
import { btnPrimary, btnSecondary, cardClass, inputClass } from './ui'

const MESSAGE_TYPES = [
  { value: 'food-beverage', label: 'Weekly special / F&B' },
  { value: 'event-promotion', label: 'Event' },
  { value: 'venue-promotion', label: 'House promo' },
]

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

const STEPS = ['Content', 'Screens', 'Schedule', 'Review']

// Moved here from the navbar (2026-09-17 per Matt): picking which venue a
// campaign targets belongs in the campaign flow itself, not a separate
// header control the user has to remember to set beforehand. Single-venue
// users have nothing to choose, so this stays hidden for them, same as the
// navbar selector it replaces.
function TargetVenueField() {
  const { user, selectVenue, inProgress } = useAuth()
  const selectingVenue = inProgress === 'SelectingVenue'
  const [venueNames, setVenueNames] = useState({})
  const [loadingVenues, setLoadingVenues] = useState(false)

  useEffect(() => {
    const fetchVenueNames = async () => {
      if (!user?.venues?.length) return
      setLoadingVenues(true)
      try {
        const venueUids = user.venues.map((v) => v.venue_uid).filter(Boolean)
        if (venueUids.length === 0) return
        const response = await fetch('/api/venues/my-venues', { credentials: 'include' })
        if (response.ok) {
          const venues = await response.json()
          const nameMap = {}
          const userVenueUids = new Set(venueUids)
          venues.forEach((venue) => {
            if (userVenueUids.has(venue.uid)) {
              nameMap[venue.uid] = `${venue.publisherName} — ${venue.venueName}`
            }
          })
          setVenueNames(nameMap)
        }
      } catch (err) {
        console.error('Failed to fetch venue names:', err)
      } finally {
        setLoadingVenues(false)
      }
    }
    fetchVenueNames()
  }, [user?.venues])

  if (!user?.hasMultipleVenues) return null

  return (
    <>
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Target venue</label>
      <select
        className={inputClass}
        value={user?.venueUid || ''}
        disabled={selectingVenue || loadingVenues}
        onChange={(e) => selectVenue(e.target.value)}
      >
        {!user?.venueUid && <option value="" disabled>Select a venue…</option>}
        {user.venues.map((venue) => (
          <option key={venue.venue_uid} value={venue.venue_uid}>
            {venueNames[venue.venue_uid] || venue.venue_uid}
          </option>
        ))}
      </select>
    </>
  )
}

export default function PublishCampaignWizard() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [step, setStep] = useState(0)
  const [files, setFiles] = useState([])
  const [messageName, setMessageName] = useState('')
  const [messageType, setMessageType] = useState('food-beverage')
  const [selectedIds, setSelectedIds] = useState([])
  const [noEnd, setNoEnd] = useState(false)
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [daysOfWeek, setDaysOfWeek] = useState([...DAYS])
  const [startTime, setStartTime] = useState('00:00')
  const [endTime, setEndTime] = useState('23:59')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [doneId, setDoneId] = useState(null)

  // Multi-venue users must pick which venue this campaign targets (Step 0's
  // TargetVenueField) before moving on -- Step 1's InventoryPicker reads the
  // same user.venueUid, so proceeding without it selected would show the
  // wrong (or no) screens. Single-venue users have nothing to pick, so this
  // is always satisfied for them.
  const venuePicked = !user?.hasMultipleVenues || !!user?.venueUid

  const canNext = useMemo(() => {
    if (step === 0) return venuePicked && files.length > 0 && messageName.trim()
    if (step === 1) return selectedIds.length > 0
    if (step === 2) return startDate && (noEnd || endDate)
    return true
  }, [step, venuePicked, files, messageName, selectedIds, startDate, endDate, noEnd])

  const toggleDay = (d) => {
    setDaysOfWeek((prev) => prev.includes(d) ? prev.filter((x) => x !== d) : [...prev, d])
  }

  const submit = async (ioAction) => {
    setBusy(true)
    setError('')
    try {
      const lineItems = [{
        type: 'screens',
        name: 'House screens',
        displayUnitIds: selectedIds,
        flight: { startDate, endDate: noEnd ? null : endDate, daysOfWeek, startTime, endTime },
      }]
      // A campaign always has a creative: canNext at step 0 requires
      // files.length > 0, so this is the only submit path. There used to be an
      // else branch posting JSON to /api/io/campaigns for the no-creative case;
      // it was unreachable, and reading it as live code hid the fact that this
      // branch was not sending selectedDeviceNames.
      const fd = new FormData()
      fd.append('advertiserName', user?.venueName || user?.name || 'House')
      fd.append('contactEmail', user?.mail || user?.email || '')
      fd.append('campaignName', messageName.trim())
      fd.append('adType', 'house-message')
      fd.append('startDate', startDate)
      if (!noEnd && endDate) fd.append('endDate', endDate)
      fd.append('recordKind', 'house_message')
      fd.append('ioAction', ioAction)
      fd.append('formDataJson', JSON.stringify({
        selectedDisplayUnitIds: selectedIds,
        // Screens sourced from Onboarding are keyed by device name, not by a
        // Control display-unit id. Send them explicitly: the server parses
        // selectedDisplayUnitIds as numbers and would drop every one of them,
        // leaving an order that looks like it selected no screens at all.
        selectedDeviceNames: selectedIds.filter(
          (v) => typeof v === 'string' && !Number.isFinite(Number(v))
        ),
        lineItems,
        ioAction,
        messageType,
        daysOfWeek,
        startTime,
        endTime,
        noEndDate: noEnd,
        durationMsec: 15000,
      }))
      files.forEach((f) => fd.append('files', f))
      fd.append('fileMetadata', JSON.stringify({
        0: { alt: messageName.trim() || 'House content' },
        [files[0]?.name]: { alt: messageName.trim() || 'House content' },
      }))
      const result = await ioSendForm('/api/orders', fd)
      setDoneId(result.id)
    } catch (e) {
      setError(e.message)
    } finally {
      setBusy(false)
    }
  }

  if (doneId) {
    return (
      <div className={`${cardClass} max-w-xl mx-auto p-8 text-center`}>
        <h1 className="text-xl font-semibold text-gray-900 dark:text-white">Message submitted</h1>
        <p className="text-sm text-gray-500 mt-2">Campaign #{doneId} is waiting for ops approval before it plays.</p>
        <button type="button" className={`${btnPrimary} mt-6`} onClick={() => navigate(`/campaigns/${doneId}`)}>
          Open campaign
        </button>
      </div>
    )
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">New campaign</h1>
        <p className="text-sm text-gray-500 mt-1">
          Advertise your venue — weekly specials, events, and in-venue promos — on your screens.
        </p>
      </div>
      <ol className="flex gap-2">
        {STEPS.map((label, i) => (
          <li
            key={label}
            className={`flex-1 text-center text-xs font-medium py-2 rounded-lg ${
              i === step
                ? 'bg-blue-600 text-white'
                : i < step
                  ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
            }`}
          >
            {i + 1}. {label}
          </li>
        ))}
      </ol>

      <div className={`${cardClass} p-6 space-y-4`}>
        {step === 0 && (
          <>
            <TargetVenueField />
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Campaign name</label>
            <input className={inputClass} value={messageName} onChange={(e) => setMessageName(e.target.value)} placeholder="Friday happy hour" />
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Type</label>
            <select className={inputClass} value={messageType} onChange={(e) => setMessageType(e.target.value)}>
              {MESSAGE_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Content</label>
            <input
              type="file"
              accept="image/*,video/mp4"
              multiple
              onChange={(e) => setFiles(Array.from(e.target.files || []))}
            />
            {files.length > 0 && (
              <ul className="text-sm text-gray-600 dark:text-gray-300">
                {files.map((f) => <li key={f.name}>{f.name}</li>)}
              </ul>
            )}
          </>
        )}
        {step === 1 && (
          <InventoryPicker
            selectedIds={selectedIds}
            onChange={setSelectedIds}
            venueUid={user?.venueUid}
            required
            title="Your screens"
            subtitle="Faces at this venue"
          />
        )}
        {step === 2 && (
          <>
            <div className="flex items-center gap-3">
              <label className="text-sm text-gray-700 dark:text-gray-300">
                <input type="radio" checked={!noEnd} onChange={() => setNoEnd(false)} className="mr-2" />
                Specific dates
              </label>
              <label className="text-sm text-gray-700 dark:text-gray-300">
                <input type="radio" checked={noEnd} onChange={() => setNoEnd(true)} className="mr-2" />
                No end date
              </label>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-gray-500">Start</label>
                <input type="date" className={inputClass} value={startDate} onChange={(e) => setStartDate(e.target.value)} />
              </div>
              {!noEnd && (
                <div>
                  <label className="text-xs text-gray-500">End</label>
                  <input type="date" className={inputClass} value={endDate} onChange={(e) => setEndDate(e.target.value)} />
                </div>
              )}
              <div>
                <label className="text-xs text-gray-500">Daily start</label>
                <input type="time" className={inputClass} value={startTime} onChange={(e) => setStartTime(e.target.value)} />
              </div>
              <div>
                <label className="text-xs text-gray-500">Daily end</label>
                <input type="time" className={inputClass} value={endTime} onChange={(e) => setEndTime(e.target.value)} />
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {DAYS.map((d) => (
                <button
                  type="button"
                  key={d}
                  onClick={() => toggleDay(d)}
                  className={`px-3 py-1 rounded-full text-xs ${
                    daysOfWeek.includes(d)
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
                  }`}
                >
                  {d}
                </button>
              ))}
            </div>
          </>
        )}
        {step === 3 && (
          <dl className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
            <div><dt className="text-gray-500">Venue</dt><dd className="text-gray-900 dark:text-white">{user?.venueName || 'N/A'}</dd></div>
            <div><dt className="text-gray-500">Name</dt><dd className="text-gray-900 dark:text-white">{messageName}</dd></div>
            <div><dt className="text-gray-500">Type</dt><dd className="text-gray-900 dark:text-white">{MESSAGE_TYPES.find((t) => t.value === messageType)?.label}</dd></div>
            <div><dt className="text-gray-500">Content</dt><dd className="text-gray-900 dark:text-white">{files.map((f) => f.name).join(', ') || 'None'}</dd></div>
            <div><dt className="text-gray-500">Screens</dt><dd className="text-gray-900 dark:text-white">{selectedIds.length} face(s)</dd></div>
            <div><dt className="text-gray-500">Flight</dt><dd className="text-gray-900 dark:text-white">{startDate} → {noEnd ? 'no end' : endDate}</dd></div>
            <div><dt className="text-gray-500">Days</dt><dd className="text-gray-900 dark:text-white">{daysOfWeek.join(', ')}</dd></div>
          </dl>
        )}
        {error && <p className="text-sm text-red-600">{error}</p>}
        <div className="flex justify-between pt-2">
          <button type="button" className={btnSecondary} disabled={step === 0 || busy} onClick={() => setStep((s) => s - 1)}>Back</button>
          {step < 3 ? (
            <button type="button" className={btnPrimary} disabled={!canNext} onClick={() => setStep((s) => s + 1)}>Next</button>
          ) : (
            <div className="flex gap-2">
              <button type="button" className={btnSecondary} disabled={busy} onClick={() => submit('draft')}>Save draft</button>
              <button type="button" className={btnPrimary} disabled={busy} onClick={() => submit('submit')}>Submit for approval</button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
